import {
  CanvasTexture,
  DoubleSide,
  Group,
  LineBasicMaterial,
  Line,
  BufferGeometry,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  SRGBColorSpace,
  Vector3,
} from 'three'
import { KNOBS, PRESETS, applyPreset, saveConfig, type PresetName, type TuningConfig } from '../physics/config'

const CANVAS_W = 420
const CANVAS_H = 620
const PANEL_W = 0.34
const PANEL_H = (PANEL_W * CANVAS_H) / CANVAS_W

const PRESET_Y = 62
const PRESET_H = 42
const ROW_TOP = 132
const ROW_H = 42
const TRACK_X = 178
const TRACK_W = 214

/**
 * Wrist-mounted tuning panel (DESIGN.md §3). This is what makes the deploy
 * loop tolerable: physics knobs change live in the headset, so a tuning pass
 * costs seconds instead of a rebuild-and-redeploy cycle.
 *
 * Interaction notes, learned the hard way on the first real flight:
 *
 *  - **Either hand can point.** Originally only the right could, which meant
 *    reaching a rocket across your body to poke your own left wrist.
 *  - **The trigger clicks.** The original binding was squeeze-only, on the
 *    reasoning that the trigger is throttle and must never fire a rocket by
 *    accident. That reasoning was right about the hazard and wrong about the
 *    fix: nobody reaches for the grip button to press a UI element. Thrust is
 *    suppressed entirely while the panel is open, so the trigger is free.
 *  - **There is a visible cursor.** A ray you cannot see the end of is not a
 *    pointer, it is a guess.
 */
export class VrPanel {
  readonly group = new Group()
  visible = false

  private readonly canvas = document.createElement('canvas')
  private readonly ctx: CanvasRenderingContext2D
  private readonly texture: CanvasTexture
  private readonly mesh: Mesh
  private readonly raycaster = new Raycaster()
  private dirty = true
  private hoverIndex = -1
  /** Canvas-space cursor, or null when nothing is aimed at the panel. */
  private cursor: { x: number; y: number } | null = null
  /** Set for one frame when the hovered row changes, so the app can buzz. */
  hoverChanged = false
  private readonly pointers: Line[] = []

  constructor(
    private readonly config: TuningConfig,
    private preset: PresetName,
    private readonly onPresetChange: (preset: PresetName) => void,
  ) {
    this.canvas.width = CANVAS_W
    this.canvas.height = CANVAS_H
    this.ctx = this.canvas.getContext('2d')!
    this.texture = new CanvasTexture(this.canvas)
    this.texture.colorSpace = SRGBColorSpace

    this.mesh = new Mesh(
      new PlaneGeometry(PANEL_W, PANEL_H),
      new MeshBasicMaterial({ map: this.texture, transparent: true, side: DoubleSide, fog: false }),
    )
    this.group.add(this.mesh)
    this.group.visible = false

    this.pointers = []
  }

  /** Panel rides the left wrist; both hands get a pointer ray. */
  attach(leftHand: Object3D, rightHand: Object3D): void {
    leftHand.add(this.group)
    // Sits above and behind the controller, angled toward the face — the same
    // place a watch would be.
    this.group.position.set(0, 0.12, 0.14)
    this.group.rotation.set(-Math.PI / 3, 0, 0)

    for (const hand of [leftHand, rightHand]) {
      const geometry = new BufferGeometry().setFromPoints([
        new Vector3(),
        new Vector3(0, 0, -1),
      ])
      const line = new Line(
        geometry,
        new LineBasicMaterial({ color: 0x6fd0ff, transparent: true, opacity: 0.55 }),
      )
      line.visible = false
      hand.add(line)
      this.pointers.push(line)
    }
  }

  setPreset(preset: PresetName): void {
    this.preset = preset
    this.dirty = true
  }

  toggle(): void {
    this.visible = !this.visible
    this.group.visible = this.visible
    for (const p of this.pointers) p.visible = this.visible
    this.cursor = null
    this.hoverIndex = -1
    this.dirty = true
  }

  cyclePreset(): PresetName {
    const names = Object.keys(PRESETS) as PresetName[]
    const next = names[(names.indexOf(this.preset) + 1) % names.length]
    this.preset = next
    applyPreset(this.config, next)
    saveConfig(this.config, next)
    this.onPresetChange(next)
    this.dirty = true
    return next
  }

  /**
   * @param hands both controller objects; whichever is aiming at the panel wins
   * @param pressed per-hand click (trigger or grip — see the class note)
   */
  update(hands: [Object3D, Object3D], pressed: [boolean, boolean]): void {
    this.hoverChanged = false
    if (!this.visible) return

    // The scene graph is written this frame but three only refreshes world
    // matrices during render, which happens after this. Force the two nodes we
    // raycast against, or the ray trails a frame behind the hand.
    this.mesh.updateWorldMatrix(true, false)

    let best: { x: number; y: number; hand: number } | null = null

    for (let i = 0; i < hands.length; i++) {
      const hand = hands[i]
      hand.updateWorldMatrix(true, false)
      hand.getWorldPosition(_origin)
      _direction.set(0, 0, -1).applyQuaternion(hand.getWorldQuaternion(_quat)).normalize()
      this.raycaster.set(_origin, _direction)

      const hit = this.raycaster.intersectObject(this.mesh, false)[0]
      const pointer = this.pointers[i]
      if (pointer) pointer.scale.setScalar(hit ? hit.distance : 0.9)
      if (hit?.uv && !best) {
        best = { x: hit.uv.x * CANVAS_W, y: (1 - hit.uv.y) * CANVAS_H, hand: i }
      }
    }

    if (best) {
      const index = rowAt(best.y)
      if (index !== this.hoverIndex) {
        this.hoverIndex = index
        this.hoverChanged = true
      }
      this.cursor = { x: best.x, y: best.y }
      this.dirty = true
      if (pressed[best.hand]) this.applyHit(best.x, best.y)
    } else if (this.cursor || this.hoverIndex !== -1) {
      this.cursor = null
      this.hoverIndex = -1
      this.dirty = true
    }

    if (this.dirty) {
      this.draw()
      this.dirty = false
    }
  }

  private applyHit(x: number, y: number): void {
    if (y >= PRESET_Y && y < PRESET_Y + PRESET_H) {
      const names = Object.keys(PRESETS) as PresetName[]
      const index = Math.floor((x / CANVAS_W) * names.length)
      const name = names[Math.max(0, Math.min(names.length - 1, index))]
      if (name !== this.preset) {
        this.preset = name
        applyPreset(this.config, name)
        saveConfig(this.config, name)
        this.onPresetChange(name)
        this.dirty = true
      }
      return
    }

    const index = rowAt(y)
    if (index < 0) return
    const knob = KNOBS[index]
    const t = Math.max(0, Math.min(1, (x - TRACK_X) / TRACK_W))
    const raw = knob.min + t * (knob.max - knob.min)
    const stepped = Math.round(raw / knob.step) * knob.step
    if (this.config[knob.key] !== stepped) {
      ;(this.config[knob.key] as number) = stepped
      saveConfig(this.config, this.preset)
      this.dirty = true
    }
  }

  /** Force a redraw, e.g. after the desktop panel changed a value. */
  invalidate(): void {
    this.dirty = true
  }

  private draw(): void {
    const ctx = this.ctx
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H)
    ctx.fillStyle = 'rgba(10, 14, 22, 0.93)'
    roundRect(ctx, 0, 0, CANVAS_W, CANVAS_H, 18)
    ctx.fill()
    ctx.strokeStyle = 'rgba(111, 208, 255, 0.35)'
    ctx.lineWidth = 2
    roundRect(ctx, 1, 1, CANVAS_W - 2, CANVAS_H - 2, 18)
    ctx.stroke()

    ctx.fillStyle = '#6fd0ff'
    ctx.font = '600 22px ui-monospace, Consolas, monospace'
    ctx.fillText('TUNING', 20, 38)

    const names = Object.keys(PRESETS) as PresetName[]
    const buttonW = CANVAS_W / names.length
    names.forEach((name, i) => {
      const active = name === this.preset
      ctx.fillStyle = active ? '#6fd0ff' : 'rgba(111, 208, 255, 0.14)'
      roundRect(ctx, i * buttonW + 8, PRESET_Y, buttonW - 16, PRESET_H - 8, 8)
      ctx.fill()
      ctx.fillStyle = active ? '#05070c' : '#dbe4f0'
      ctx.font = '600 18px ui-monospace, Consolas, monospace'
      ctx.textAlign = 'center'
      ctx.fillText(name, i * buttonW + buttonW / 2, PRESET_Y + 26)
      ctx.textAlign = 'left'
    })

    KNOBS.forEach((knob, i) => {
      const y = ROW_TOP + i * ROW_H
      const value = this.config[knob.key] as number
      const t = Math.max(0, Math.min(1, (value - knob.min) / (knob.max - knob.min)))

      if (i === this.hoverIndex) {
        ctx.fillStyle = 'rgba(111, 208, 255, 0.10)'
        ctx.fillRect(6, y - 20, CANVAS_W - 12, ROW_H - 6)
      }

      ctx.fillStyle = '#8a97ab'
      ctx.font = '15px ui-monospace, Consolas, monospace'
      ctx.fillText(knob.label, 18, y)

      ctx.fillStyle = 'rgba(120, 160, 220, 0.22)'
      roundRect(ctx, TRACK_X, y - 12, TRACK_W, 10, 5)
      ctx.fill()
      ctx.fillStyle = '#6fd0ff'
      roundRect(ctx, TRACK_X, y - 12, Math.max(6, TRACK_W * t), 10, 5)
      ctx.fill()

      ctx.fillStyle = '#dbe4f0'
      ctx.font = '14px ui-monospace, Consolas, monospace'
      ctx.textAlign = 'right'
      ctx.fillText(value.toFixed(value >= 10 ? 0 : 2), CANVAS_W - 16, y + 16)
      ctx.textAlign = 'left'
    })

    ctx.fillStyle = '#8a97ab'
    ctx.font = '13px ui-monospace, Consolas, monospace'
    ctx.fillText('point + TRIGGER · X closes · Y cycles preset', 18, CANVAS_H - 34)
    ctx.fillText('thrust is off while this is open', 18, CANVAS_H - 16)

    if (this.cursor) {
      ctx.beginPath()
      ctx.arc(this.cursor.x, this.cursor.y, 7, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(111, 208, 255, 0.9)'
      ctx.fill()
      ctx.strokeStyle = '#05070c'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    this.texture.needsUpdate = true
  }
}

function rowAt(y: number): number {
  const index = Math.floor((y - ROW_TOP + 20) / ROW_H)
  return index >= 0 && index < KNOBS.length ? index : -1
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

const _origin = new Vector3()
const _direction = new Vector3()
const _quat = new Quaternion()
